// @flow

import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';

import TourBlockoutService from '../../services/tourBlockoutService';
import type {
  TourBlockout,
  TourBlockoutPayload,
} from '../../containers/TourBlockouts/types';

export const tourBlockoutsQueryKey = (
  organizationId: ?string,
  propertyId: ?string,
): Array<?string> => ['tour-blockouts', organizationId, propertyId];

// A property's Tour Blockouts. Disabled until the organization and property
// are known. Not retried: a refusal (flag off, no permission) won't change on
// a retry, and the screen shows the backend's message instead.
export const useTourBlockoutsQuery = ({
  organizationId,
  propertyId,
}: {
  organizationId: ?string,
  propertyId: ?string,
}) => {
  const service = useMemo(() => new TourBlockoutService(), []);
  return useQuery<Array<TourBlockout>>(
    tourBlockoutsQueryKey(organizationId, propertyId),
    () => {
      if (!organizationId || !propertyId) {
        throw new Error('organizationId and propertyId are required');
      }
      return service.getTourBlockoutList(organizationId, propertyId);
    },
    {
      enabled: Boolean(organizationId && propertyId),
      refetchOnWindowFocus: false,
      useErrorBoundary: false,
      retry: false,
    },
  );
};

// Create when no id is given, otherwise replace. Refreshes the list on success.
export const useSaveTourBlockout = ({
  organizationId,
  propertyId,
}: {
  organizationId: string,
  propertyId: string,
}) => {
  const service = useMemo(() => new TourBlockoutService(), []);
  const queryClient = useQueryClient();
  return useMutation<
    TourBlockout,
    mixed,
    { id: ?string, payload: TourBlockoutPayload },
  >(
    ({ id, payload }) =>
      id
        ? service.updateTourBlockout(organizationId, propertyId, id, payload)
        : service.createTourBlockout(organizationId, propertyId, payload),
    {
      onSuccess: () =>
        queryClient.invalidateQueries(
          tourBlockoutsQueryKey(organizationId, propertyId),
        ),
    },
  );
};

export const useDeleteTourBlockout = ({
  organizationId,
  propertyId,
}: {
  organizationId: string,
  propertyId: string,
}) => {
  const service = useMemo(() => new TourBlockoutService(), []);
  const queryClient = useQueryClient();
  return useMutation<{ id: string }, mixed, string>(
    (id) => service.deleteTourBlockout(organizationId, propertyId, id),
    {
      onSuccess: () =>
        queryClient.invalidateQueries(
          tourBlockoutsQueryKey(organizationId, propertyId),
        ),
    },
  );
};
